import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "../slugs/vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const vipMinibus: ChauffeurPage = {
  slug: "vip-minibus",
  title: "VIP minibus with chauffeur — group transport",
  subtitle:
    "Mercedes Sprinter VIP for up to 16 passengers with chauffeur across the French Riviera.",
  keywords: [
    "VIP minibus with chauffeur",
    "group transport Cannes",
    "group airport shuttle Nice",
    "VIP van French Riviera",
  ],
  hero: {
    title:
      "VIP minibus with chauffeur — up to 16 passengers on the French Riviera",
    subtitle:
      "Mercedes Sprinter VIP: travel together without compromising on comfort. Airport transfers, events, conferences and weddings.",
    keywords: ["VIP minibus", "group transport", "Sprinter chauffeur"],
    imageSrc: heroImage.src,
    imageAlt:
      "VIP Mercedes Sprinter minibus with chauffeur on the French Riviera",
    badges: ["Up to 16 passengers", "14 luggage", "Dedicated chauffeur"],
  },
  benefits: {
    heading: "Why the VIP minibus with KS Rent Services",
    items: [
      {
        title: "Travel together",
        description:
          "The whole group in a single vehicle: no convoys split across several cars — you stay together from departure to arrival.",
      },
      {
        title: "Space & luggage",
        description:
          "Up to 16 passengers and 14 bags: ideal for airport transfers, yacht crews and group stays.",
      },
      {
        title: "VIP comfort",
        description:
          "Refined upholstery, air conditioning, charging sockets and generous space: the Sprinter in VIP configuration, not a standard shuttle.",
      },
      {
        title: "Dedicated chauffeur",
        description:
          "A single point of contact, a punctual chauffeur and the personal welcome of a high-end chauffeur service.",
      },
    ],
  },
  services: {
    heading: "Group transport services",
    services: [
      {
        title: "Group airport transfers",
        text: "Nice airport, stations and Monaco heliport: the whole group and its luggage in a single trip.",
      },
      {
        title: "Events & conferences",
        text: "Conventions, festival, corporate evenings and incentives: coordinated shuttles for your teams and guests.",
      },
      {
        title: "Weddings & families",
        text: "Processions, guest transfers and family travel, serene and punctual.",
      },
      {
        title: "Hourly hire",
        text: "VIP minibus and chauffeur by the hour or day, with the same contact throughout your programme.",
      },
    ],
  },
  locations: {
    heading: "Areas served",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & airport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "The group together, the premium experience",
    subheading:
      "The capacity of a minibus, the standing of a private chauffeur service.",
    paragraphs: [
      "The Mercedes Sprinter VIP brings up to 16 people together in a single comfortable vehicle, without splitting your group across several cars or multiplying chauffeurs.",
      "Every trip is prepared with you: schedules, pick-up points and routes are coordinated so everyone arrives together and on time.",
      "You keep a single point of contact, transparent airport packages and the flexibility to adjust your programme at the last minute.",
    ],
    bullets: [
      "Up to 16 passengers and 14 bags",
      "Transparent airport packages",
      "Available 24/7",
    ],
  },
  process: {
    heading: "How to book your VIP minibus",
    steps: [
      "Tell us your route, your schedule and the number of passengers.",
      "Receive a clear package or a per-kilometre estimate.",
      "Assignment of the Sprinter VIP and your dedicated chauffeur.",
      "Grouped, punctual and comfortable pick-up.",
    ],
  },
  faq: [
    {
      q: "How many passengers does the VIP minibus carry?",
      a: "The Mercedes Sprinter VIP carries up to 16 passengers and 14 bags, ideal for groups, families and crews.",
    },
    {
      q: "Which events do you cover?",
      a: "Conventions, festival, conferences, corporate evenings, weddings and group transfers: we coordinate shuttles for your teams and guests.",
    },
    {
      q: "Do you handle group airport transfers?",
      a: "Yes, to and from Nice airport in particular, with transparent packages for the whole group and its luggage in a single trip.",
    },
    vehiclePriceFaq("en", "CLASS_SPRINTER", "a VIP minibus with chauffeur"),
    {
      q: "How do I book?",
      a: "Tell us your route, your schedule and the number of passengers: we quickly confirm the Sprinter VIP and your dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    { label: "See every journey", href: "/en/chauffeur/transfers" },
    { label: "Corporate chauffeur", href: "/en/chauffeur/business" },
    { label: "Chauffeur for events", href: "/en/chauffeur/events" },
    {
      label: "Chauffeur-driven wedding car",
      href: "/en/chauffeur/wedding-car-cannes",
    },
  ],
  cta: {
    title: "A group to transport?",
    text: "Tell us your route and party size — fast quote for your VIP minibus with chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Request a group quote",
  },
  seo: {
    ...vehicleSeo(
      "en",
      "CLASS_SPRINTER",
      "VIP minibus with chauffeur, 16 seats",
      "Sprinter VIP 16-seat minibus with chauffeur",
    ),
    canonical: "/en/chauffeur/vip-minibus",
  },
};

export default vipMinibus;
